/**
 * Core Type Definitions for Layout Editor
 * These types are designed to be renderer-agnostic and support both 2D and future 3D rendering
 */

// Available element types in the toolbox.
// AMHS component classes (BriteLab hardware catalog) plus generic annotation shapes.
export type ElementType =
  | 'overhead-rail' // Overhead Rail (AeroTrak family, branch switch, vertical lift)
  | 'overhead-curve' // Overhead Rail Curve (AeroTrak family, branch switch, vertical lift)
  | 'cross-rail' // Circular junction (and future shared junction types)
  | 'oht-vehicle' // OHT Vehicle (AeroLoader family)
  | 'stocker' // Stocker (7000 Series, TurboStocker 2)
  | 'stb' // Side Track Buffer
  | 'ohb' // Overhead Hoist Buffer
  | 'lifter' // Lifter / vertical transfer module
  | 'support-pillar' // Support pillar for overhead track system
  | 'horizontal-beam' // Horizontal beam connecting pillars
  | 'suspension-rod' // Vertical rod hanging from beam to track
  | 'rectangle' // Annotation: generic area / tool footprint
  | 'circle' // Annotation: zone
  | 'text'; // Annotation: label

// Predefined 3D camera view angles & modes
export type CameraViewPreset =
  | 'default'
  | 'reference'
  | 'front45'
  | 'wide'
  | 'top'
  | 'front'
  | 'back'
  | 'left'
  | 'right'
  | 'follow'
  | 'focus';

/** Third-person framing while Follow OHT is active. */
export type FollowOhtCameraMode = 'chase' | 'side' | 'elevated';

// Component class groupings used for color-coding and categorisation
export type ComponentClass =
  | 'overhead-rail'
  | 'overhead-curve'
  | 'cross-rail'
  | 'oht-vehicle'
  | 'stocker'
  | 'stb'
  | 'ohb'
  | 'lifter'
  | 'support-pillar'
  | 'horizontal-beam'
  | 'suspension-rod';

// Position in 2D space; z is 3D elevation only (canvas units, not used on 2D canvas).
export type Position = {
  x: number;
  y: number;
  /** 3D elevation in canvas units. Editable in Properties; ignored by 2D view. */
  z?: number;
};

// Dimensions for layout elements
export type Dimensions = {
  width: number;
  height: number;
  depth?: number; // Future 3D support
  thickness?: number; // For walls, doors, etc.
};

// Element metadata
export type ElementMetadata = {
  name: string;
  locked: boolean;
  visible: boolean;
  color?: string;
  opacity?: number;
  zIndex?: number;
  layerId?: string; // which user layer this element belongs to
  groupId?: string; // group membership (for move-together groups)
};

// Main layout element interface
export type LayoutElement = {
  id: string;
  layerId: string;
  type: ElementType;
  position: Position;
  rotation: number; // In degrees
  dimensions: Dimensions;
  metadata: ElementMetadata;
  parentId?: string; // For grouping elements
  children?: string[]; // Child element IDs
  customProperties?: Record<string, unknown>; // For extensibility
};

// Toolbox item definition. Each item represents a concrete SKU from the
// BriteLab catalog (e.g. "AeroTrak AT2") that maps to an ElementType class.
export const TOOLBOX_BOM_UNITS = [
  { value: 'pcs', label: 'piece(s)' },
  { value: 'm', label: 'm' },
  { value: 'mm', label: 'mm' },
  { value: 'kg', label: 'kg' },
  { value: 'L', label: 'L' },
] as const;

export type ToolboxBomUnit = (typeof TOOLBOX_BOM_UNITS)[number]['value'];

export type ToolboxBomLine = {
  id: string;
  name: string;
  qty: number;
  category?: string;
  description?: string;
  unit: ToolboxBomUnit;
};

export type ToolboxCustomField = {
  id: string;
  label: string;
  value: string;
  unit?: string;
};

export type ToolboxItem = {
  id: string;
  type: ElementType;
  name: string;
  icon: string;
  defaultDimensions: Dimensions;
  category?: string;
  /** Backend product-type / sidebar category id (toolbox catalog API). */
  productTypeId?: string;
  sku?: string;
  description?: string;
  // Parametric defaults copied into the element's customProperties on placement
  defaultProperties?: Record<string, unknown>;
  /** Initial rotation in degrees when placed on the canvas (default 0). */
  defaultRotation?: number;
  /** Optional BOM component rows for Tools Configuration. */
  bom?: ToolboxBomLine[];
  /** Optional mass for physical footprint (Tools Configuration). */
  weightKg?: number;
  /** When false, hide from the left toolbox after save. Default true. */
  visibleInToolbox?: boolean;
  /** Extra user-defined configuration fields. */
  customFields?: ToolboxCustomField[];
  /** Keys of standard parameters enabled for this tool. */
  enabledParams?: string[];
};

// Editable parametric field definition for a component class
export type ComponentField = {
  key: string;
  label: string;
  type: 'select' | 'number' | 'text' | 'boolean';
  options?: string[];
  unit?: string;
};

// Layer definition for layer management
export type Layer = {
  id: string;
  name: string;
  visible: boolean;
  locked: boolean;
  color?: string;
  zIndex: number;
  elementIds: string[];
};

// Group of elements that move together and can be locked as a unit
export type Group = {
  id: string;
  name: string;
  locked: boolean;
  elementIds: string[];
};

// Selection state
export type SelectionState = {
  selectedIds: string[];
  hoveredId: string | null;
  selectionBox: {
    startX: number;
    startY: number;
    endX: number;
    endY: number;
  } | null;
};

// Canvas viewport state
export type ViewportState = {
  x: number;
  y: number;
  scale: number; // Zoom level
};

// Grid settings
export type GridSettings = {
  enabled: boolean;
  /** Visual grid line spacing in world units. */
  size: number;
  /** Independent snap lattice spacing in world units. */
  snapGridSize: number;
  snapToGrid: boolean;
  snapToObjects: boolean;
  color: string;
  opacity: number;
};

// Alignment guide
export type AlignmentGuide = {
  id: string;
  type: 'vertical' | 'horizontal';
  position: number;
  elementIds: string[];
};

// History action for undo/redo
export type HistoryAction = {
  id: string;
  type:
    | 'add'
    | 'remove'
    | 'update'
    | 'move'
    | 'resize'
    | 'rotate'
    | 'group'
    | 'ungroup'
    | 'replace';
  timestamp: number;
  // For 'replace': the ids of the newly-added elements (removed on undo).
  elementIds: string[];
  // For 'replace': the full elements removed by this action (restored on undo).
  previousState?: Partial<LayoutElement>[];
  // For 'replace': the full elements added by this action (re-added on redo).
  newState?: Partial<LayoutElement>[];
};

import type { EquipmentLayerData } from '../features/equipment/equipmentTypes';
import type { StockerExportDoc } from '../utils/stockerExport';

// Application state for serialization
/** User-assigned role for a simulation track network node (entry/exit/junction landmarks). */
export type TrackNodeLandmarkRole = 'none' | 'entry' | 'exit' | 'junction';

/**
 * Persisted metadata for a track node, keyed by world position so it survives
 * graph rebuilds when rail geometry changes and node indices shift.
 */
export type TrackNodeAnnotation = {
  x: number;
  y: number;
  name: string;
  role: TrackNodeLandmarkRole;
  /** FOUP hold limit for Entry/Exit gate ports (exported as loadPort.foupCapacity). */
  foupCapacity?: number;
  /**
   * When true, this node is also a Junction/Switch (e.g. Entry/Exit sitting on a
   * corner divert). Pure divert markers use role `"junction"` instead.
   */
  isJunction?: boolean;
};

export type LayoutProject = {
  /** Present on exports that bundle layout + identified equipment. */
  kind?: 'fab-project';
  version: string;
  metadata: {
    name: string;
    createdAt: string;
    updatedAt: string;
    author?: string;
    /** Imported DXF name this layout was authored against (optional). */
    floorPlanName?: string | null;
    /** Optional inventory / badge / process-gate rules (AMHS layouts). */
    inventory?: Record<string, unknown>;
    /** Optional transport-flow overrides (AMHS layouts). */
    transportFlow?: Record<string, unknown>;
    [key: string]: unknown;
  };
  elements: Record<string, LayoutElement>;
  elementOrder?: string[];
  layers: Record<string, Layer>;
  layerOrder?: string[];
  groups?: Record<string, Group>;
  viewport: ViewportState;
  gridSettings: GridSettings;
  /** Named entry/exit (and display names) for simulation track nodes. */
  trackNodeAnnotations?: TrackNodeAnnotation[];
  /** Semantic equipment overlay (identified regions), separate from AMHS elements. */
  equipment?: EquipmentLayerData;
  /**
   * @deprecated Legacy separate stocker export block. Newer saves put DXF stockers
   * into `elements` as normal `type: 'stocker'` entries. Still read on load.
   */
  stockers?: StockerExportDoc;
  simulation?: {
    speed: number;
    vehicles: Record<
      string,
      {
        reversed: boolean;
        stopNodeId: number | null;
        speedFactor: number;
        stopDurationS: number | null;
        stops?: Array<{
          id: string;
          railId: string;
          distance: number;
          durationS: number | null;
          setloaded?: boolean;
        }>;
      }
    >;
  };
};

// Tool types for the active tool
export type ToolType = 'select' | 'pan' | 'draw' | ElementType;

/** Display unit for distances shown/edited across the app (properties, track labels). */
export type MeasurementUnit = 'm' | 'mm' | 'in' | 'ft';

// Rail topology validation results (F2.2)
export type ValidationConflict = {
  aId: string;
  bId: string;
};

export type ValidationState = {
  hasRun: boolean;
  conflicts: ValidationConflict[];
  openEndpoints: { x: number; y: number }[];
  loops: number;
  railCount: number;
  closed: boolean;
};

// Imported DXF floor plan (rendered as a background reference)
export type FloorPlanBounds = {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
};

// A single show/hideable layer of an imported DXF floor plan.
export type DxfLayer = {
  /** Backend layer UUID when available (required to persist visibility). */
  id?: string;
  name: string;
  color: number; // AutoCAD Color Index (ACI)
  category?: string;
  subcategory?: string;
  visible: boolean;
  frozen?: boolean;
  locked?: boolean;
  count: number; // number of drawable polylines on this layer
};

// Per-polyline source provenance, aligned 1:1 with FloorPlanState.polylines.
export type DxfPolylineMeta = {
  type: string; // LINE | LWPOLYLINE | POLYLINE | ARC | CIRCLE
  color: number; // entity ACI color (256 = ByLayer)
  category?: string; // source entity primary_category
  subcategory?: string; // source entity sub_category
};

// A drawable text label extracted from a DXF TEXT entity, in canvas space.
export type DxfTextLabel = {
  x: number;
  y: number;
  text: string;
  height: number; // font cap height in canvas units
  rotation: number; // degrees, canvas space
  layerIndex: number; // index into FloorPlanState.layers
  color: number; // entity ACI color (256 = ByLayer)
};

// Raw-DXF → normalised-canvas mapping (Y is flipped before translate + scale).
export type FloorPlanTransform = {
  rawMinX: number;
  rawMinY: number;
  invScale: number;
};

// Where the physical-scale calibration of an imported drawing came from.
export type CalibrationSource = 'header' | 'dimension' | 'scale_bar' | 'annotation' | 'unknown';

// Physical interpretation of an imported drawing's coordinate space. Ratios are
// null when the units could not be determined (resolved=false).
export type DrawingCalibration = {
  unit: 'mm' | 'cm' | 'm' | 'in' | 'ft' | null;
  drawingUnitsPerMeter: number | null;
  metersPerDrawingUnit: number | null;
  millimetersPerDrawingUnit: number | null;
  source: CalibrationSource;
  confidence: number;
  resolved: boolean;
  details?: string;
};

export type FloorPlanState = {
  name: string | null;
  /** Backend floor-plan UUID when loaded from API; null for local/untitled plans. */
  floorPlanId: string | null;
  polylines: number[][];
  bounds: FloorPlanBounds | null;
  /** Mapping used to project external raw-DXF coordinates onto the canvas. */
  transform: FloorPlanTransform | null;
  /**
   * Raw DXF units per canvas unit. e.g. 20 means 1 canvas unit = 20 mm.
   * Used to size components proportionally to the imported floor plan.
   */
  scaleFactor: number;
  dxfUnit: 'mm' | 'cm' | 'm' | 'in' | 'ft';
  /** Source DXF units per meter, or 0 when the units could not be determined. */
  dxfUnitsPerMeter: number;
  /** Authoritative physical calibration (drawing units ↔ real world). */
  calibration: DrawingCalibration | null;
  /** Millimetres represented by one canvas unit (null when units are unknown). */
  millimetersPerCanvasUnit: number | null;
  /** Canvas units per meter (null when units are unknown). */
  canvasUnitsPerMeter: number | null;
  visible: boolean;
  /**
   * Global opacity for all visible DXF floor-plan layers (0–1).
   * Applied in both 2D and 3D renderers.
   */
  opacity: number;
  /** Whether imported text labels are rendered on the canvas. */
  showLabels: boolean;
  /** Layers of the imported DXF, each independently show/hideable. */
  layers: DxfLayer[];
  /** For each polyline (aligned to `polylines`), the index of its layer. */
  polylineLayers: number[];
  /** Per-polyline source metadata (type/color), aligned to `polylines`. */
  polylineMeta: DxfPolylineMeta[];
  /** Drawable text labels from the imported DXF. */
  labels: DxfTextLabel[];
  /**
   * Hidden entity-type keys within a layer. Key format: "${layerIndex}:${entityType}"
   * (e.g. "2:LINE"). Absence of a key means visible. Stored sparse so it's small
   * even with 100k polylines.
   */
  hiddenEntityTypes: Record<string, boolean>;
  /** Index of the polyline the user last clicked/focused on the canvas. */
  focusedPolylineIndex: number | null;
  /** Index of the DXF layer currently hovered in the panel. Null if none. */
  hoveredLayerIndex: number | null;
  /**
   * Last-saved (or load-time) visibility per layer index. Used to detect dirty
   * toggles before posting visibility updates to the backend.
   */
  layerVisibilityBaseline: boolean[];
  /** Layer indices whose `visible` differs from `layerVisibilityBaseline`. */
  dirtyLayerIndices: number[];
};

// ─── Facility analysis (semantic region overlay) ──────────────────────────────

// A single analysed region/zone, with its rectangle already projected into
// normalised canvas space (aligned to the imported floor plan).
export type FacilityRegion = {
  id: string;
  name: string;
  type: string; // equipment_area | process_bay | cleanroom | unclassified | ...
  confidence: number; // 0..1
  // Rectangle in normalised canvas space.
  x: number;
  y: number;
  width: number;
  height: number;
  layer: string;
  equipmentCount: number;
  equipment: string[];
};

export type FacilityState = {
  name: string | null;
  regions: FacilityRegion[];
  /** Master overlay visibility. */
  visible: boolean;
  /** Per-type visibility, keyed by region type. */
  visibleTypes: Record<string, boolean>;
  /** Only show regions whose confidence ≥ this threshold. */
  minConfidence: number;
  /** Currently selected region (from the panel or a canvas click). */
  selectedId: string | null;
};

// UI state
export type UIState = {
  activeTool: ToolType;
  rightDrawerTab:
    | 'properties'
    | 'dxf-layers'
    | 'layers'
    | 'facility'
    | 'equipment'
    | 'elements'
    | '3ddepths';
  gridSettings: GridSettings;
  showGrid: boolean;
  showRulers: boolean;
  showAlignmentGuides: boolean;
  darkMode: boolean;
  leftSidebarOpen: boolean;
  rightSidebarOpen: boolean;
  bottomPanelOpen: boolean;
  dxfLayersPanelOpen: boolean;
  facilityPanelOpen: boolean;
  simulationPanelOpen: boolean;
  view3dOpen: boolean;
  /** True once the 3D canvas has presented a warm frame (hides 2D + loader). */
  view3dSceneRevealed: boolean;
  /** Predefined camera view preset ('default', 'top', 'front', 'back', 'left', 'right', 'follow', 'focus'). */
  active3DCameraPreset: CameraViewPreset;
  /** Version counter incremented whenever a camera preset is selected to force re-positioning. */
  cameraPresetVersion?: number;
  /** OHT vehicle whose camera the 3D view is currently riding, or null for the default free camera. */
  active3DCameraOhtId: string | null;
  /** OHT the chase camera is following. Many OHTs can run; one camera follows one vehicle. */
  active3DFollowOhtId: string | null;
  /** Follow OHT third-person framing (chase / side / elevated). */
  active3DFollowOhtMode: FollowOhtCameraMode;
  /** Editor workspace: floor-plan map or process mindmap (no route change). */
  workspaceView: 'map' | 'mindmap';
  /** Full-page Tools Configuration overlay is open. */
  toolboxSettingsOpen: boolean;
  /** Mindmap right drawer uses a wider layout when true. */
  mindmapDrawerExtended: boolean;
  /** Left-drag pans the layout instead of orbiting the camera. */
  view3dDragLayout: boolean;
  /** Entered via the 3D Simulation toolbar button. */
  view3dSimulation: boolean;
  zoomLevel: number;
  placingStopForVehicle: string | null;
  /** Global unit for distance display and editing (properties panel, track labels). */
  measurementUnit: MeasurementUnit;
  propertiesPanelTab: 'properties' | '3ddepths';
  extrusionSettings: {
    walls: boolean;
    equipment: boolean;
    furniture: boolean;
    tracks: boolean;
  };
  /** Show or hide support pillars and hanging suspension rods in 3D scene. */
  showSupportPillars: boolean;
  /** Highlight simulation track-network nodes on the 2D canvas. */
  showSimulationTrackNodes: boolean;
  /** Show FOUP pickup-ready labels on tools during a live simulation. */
  showFoupPickupLabels: boolean;
  /** Show persistent OHT name labels on the 2D canvas. */
  showOhtLabels: boolean;
  /** Show Travel / Pick From / Drop To chips on OHTs during simulation. */
  showOhtRouteLabels: boolean;
  /** Click-to-place mode for splitting straight tracks into additional network nodes. */
  trackNodePlacementActive: boolean;
  /**
   * LLM-route start/end capture: which endpoint the next canvas click sets ('start' → 'end' → null),
   * and the captured world-space points. Used to force the generated route to reach these points.
   */
  routeEndpointPlacement: 'start' | 'end' | null;
  routeStartPoint: { x: number; y: number } | null;
  routeEndPoint: { x: number; y: number } | null;
  /**
   * Point-draw sketcher: click successive points; each consecutive pair
   * becomes a segment. Tracks the pending polyline + active mode.
   */
  pointDraw: {
    active: boolean;
    /** Committed clicked vertices (world units) forming the pending polyline. */
    vertices: { x: number; y: number }[];
    /** Live pointer position for the rubber-band segment preview. */
    preview: { x: number; y: number } | null;
  };
  trackDraw: {
    active: boolean;
    head: string | null;
    segmentType: string;
  };
};

// Transform controls
export type TransformControls = {
  position?: Partial<Position>;
  rotation?: number;
  dimensions?: Partial<Dimensions>;
};

// Bounding box for selection and collision detection
export type BoundingBox = {
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
};

// Renderer interface for abstracting 2D/3D rendering
export type SceneRenderer = {
  render(elements: LayoutElement[]): void;
  updateElement(element: LayoutElement): void;
  removeElement(elementId: string): void;
  clear(): void;
};

// Export/Import formats
export type ExportFormat = 'json' | 'png' | 'svg' | 'pdf';

// Keyboard shortcut action types
export type ShortcutAction =
  | 'undo'
  | 'redo'
  | 'delete'
  | 'duplicate'
  | 'selectAll'
  | 'copy'
  | 'paste'
  | 'deselect'
  | 'zoomIn'
  | 'zoomOut'
  | 'fitToScreen'
  | 'toggleGrid'
  | 'save';

export interface CircleJunctionConnection {
  id: string;
  fromPortId: string;
  toPortId: string;
}
